import axios from 'axios'
import { v4 } from 'uuid'
import { lib } from '@gocanvas/client'
const {
  getValue,
  getOperationType,
  conditionCheck,
  LOOP_SHEET_TYPE_IDS,
  guid,
  isAdvancedLoop,
  isLoop,
  isList,
  orderByPosition,
} = lib
const { get, post, patch } = axios

const getToken = () => document.querySelector('[name="csrf-token"]').content
const buildHeaders = (isFormData = false) => ({
  headers: {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'X-CSRF-Token': getToken(),
  },
})

export const csrfFetch = url => get(url, buildHeaders())

export const fetchConditionTypes = () => csrfFetch('/condition_types.json')

// internal format includes workflow data
export const getForm = async form_id => {
  const formEndpoint = `/api/v3/forms/${form_id}?format=internal`

  return get(formEndpoint)
}

export const getWebForm = async form_id => {
  const formEndpoint = `/api/v3/forms/${form_id}?format=web_form`

  return get(formEndpoint)
}

export const getReferenceData = async form_id => {
  const refDataEndpoint = `/api/v3/reference_data?form_id=${form_id}`

  return get(refDataEndpoint)
}

export const getUserDepartmentsForForm = async form_id => {
  const departmentsEndpoint = `/api/v3/forms/${form_id}/user_departments`

  return get(departmentsEndpoint)
}

export const getOnboardingReferenceData = async form_id => {
  const refDataEndpoint = `/reference_datas.json?form_id=${form_id}`

  return get(refDataEndpoint).then(response => {
    const { data } = response

    return {
      data: data.map(file => ({
        ...file,
        headers: file.reference_columns, // Ensure each file has a unique content_guid
      })),
    }
  })
}

export const fetchLiveDataSources = (departmentId, formId) =>
  get(`/api/v3/live_data_sources?status=published&department_id=${departmentId}${formId ? `&form_id=${formId}` : ''}`)

export const fetchFormData = async formId => {
  const { initialOnboardingState = {} } = window || {}
  const {
    user = {},
    live_data_enabled,
    current_department_id
  } = initialOnboardingState
  const { appId } = user
  const id = formId || appId

  if (id) {
    const [
      formResponse,
      conditionTypesResponse,
      refDataResponse,
      departmentsResponse,
      liveDataResponse,
    ] = await Promise.all([
      getForm(id),
      fetchConditionTypes(),
      getReferenceData(id).catch(() => ({ data: [] })),
      getUserDepartmentsForForm(id).catch(() => ({ data: [] })),
      live_data_enabled
        ? fetchLiveDataSources(current_department_id, formId).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
    ])

    const { data: formData } = formResponse
    const { data: conditionTypes } = conditionTypesResponse
    const { data: refData } = refDataResponse
    const { data: departments = [] } = departmentsResponse || {}
    const { data: liveDataSources = [] } = liveDataResponse

    const { entities } = unpackFlatApp(formData)
    return {
      ...entities,
      conditionTypes: conditionTypes.map(unpackConditionType),
      referenceData: refData,
      departments,
      liveDataSources,
      dataSourceContextParams: formData.data_source_context_params || [],
      dataSourceContexts: formData.data_source_contexts || [],
    }
  } else {
    throw new Error('No formId or appId provided')
  }
}

export const fetchOnboardingFormData = async formId => {
  const { initialOnboardingState = {} } = window || {}
  const { user = {} } = initialOnboardingState
  const { appId } = user
  const id = formId || appId

  if (id) {
    const [formResponse, conditionTypesResponse, refDataResponse] =
      await Promise.all([
        getForm(id),
        fetchConditionTypes(),
        getOnboardingReferenceData(id),
      ])

    const { data: formData } = formResponse
    const { data: conditionTypes } = conditionTypesResponse
    const { data: refData } = refDataResponse

    const { entities } = unpackFlatApp(formData)

    return {
      ...entities,
      conditionTypes: conditionTypes.map(unpackConditionType),
      refData,
    }
  } else {
    throw new Error('No formId or appId provided')
  }
}

export const fetchWebFormData = async () => {
  const { initialOnboardingState = {} } = window || {}
  const { appId, webFormLogo } = initialOnboardingState

  if (appId) {
    const { data: formData } = await getWebForm(appId)
    const { data: conditionTypes } = await fetchConditionTypes()
    const { entities } = unpackFlatApp(formData)
    const { app } = entities
    return {
      entities: {
        ...entities,
        app: {
          ...app,
          logo: webFormLogo,
        },
      },
      types: {
        conditionTypes: conditionTypes.map(unpackConditionType),
      },
    }
  }
}

const linkConditions = (conditions, entries) => {
  return conditions.map(({ entry_id, parent_entry_id, ...condition }) => {
    const parentEntry = entries.find(({ id }) => id === parent_entry_id) || {}
    return {
      parentEntryGuid: parentEntry.guid,
      ...condition,
    }
  })
}

const linkOperations = operations => {
  return operations.map(({ operation_type_id, ...operation }, i) => {
    return {
      operation_type_id,
      operation_type: getOperationType(operation_type_id),
      index: i,
      ...operation,
    }
  })
}

const linkMultiSection = sheets =>
  sheets.map(({ multi_section, ...sheet }) => ({
    ...sheet,
    multi_section: multi_section ? multi_section.id : multi_section,
  }))

const normalizeEntryTypes = entry => {
  const type = entry.type == 'Summary' ? 'Sum' : entry.type
  return {
    ...entry,
    type,
  }
}

export const unpackFlatApp = ({
  operations,
  conditions,
  entries,
  sheets,
  sections,
  ...app
}) => {
  const normalizedEntries = entries.map(normalizeEntryTypes)
  return {
    entities: {
      operations: linkOperations(operations),
      conditions: linkConditions(conditions, normalizedEntries),
      entries: normalizedEntries,
      sheets: linkMultiSection(sheets),
      sections,
      app,
    },
  }
}

export const unpackConditionType = ({ condition_type }) => condition_type

const fieldTypeTransforms = entry => {
  const { type, label, value, length, ...rest } = entry

  // image fields should create a content_guid, later used as a reference
  if (['Signature', 'Drawing'].includes(type) && value) {
    return {
      type,
      label,
      ...rest,
      value: value?.[0]?.url,
      content_guid: v4(),
    }
  }

  if (type === 'Photo') {
    if (value && value.length) {
      if (length > 1) {
        return {
          type,
          label,
          ...rest,
          content_guids: value.map(({ url }) => ({ url, content_guid: v4() })),
        }
      } else {
        return {
          type,
          label,
          ...rest,
          value: value?.[0]?.url,
          content_guid: v4(),
        }
      }
    }
  }

  if (type === 'Video' || type === 'Attachment') {
    if (value && value.length) {
      if (length > 1) {
        return {
          type,
          label,
          ...rest,
          content_guids: value.map(({ url, name }) => ({
            url,
            content_guid: v4(),
            filename: name,
          })),
        }
      } else {
        return {
          type,
          label,
          ...rest,
          value: value?.[0]?.url,
          content_guid: v4(),
          filename: value?.[0]?.name,
        }
      }
    }
  }

  if (type === 'Static Text') {
    return {
      ...entry,
      value: value || label || '',
    }
  }

  return {
    ...entry,
    value: getValue(type, value),
  }
}

const fetchBlobAppend = (formData, content_guid, value, filename) =>
  fetch(value)
    .then(response => response.blob())
    .then(blob => {
      // Create a File object with the proper filename
      const fileWithName = new File([blob], filename || 'file', {
        type: blob.type,
      })
      formData.append(content_guid, fileWithName)
    })

// extracts any files and appends them to the form
async function extractFiles(formData, transformedData) {
  await Promise.all(
    transformedData
      .filter(
        ({ content_guid, content_guids }) => content_guid || content_guids
      )
      .flatMap(({ content_guid, content_guids, value, filename }) => {
        if (content_guid) {
          return [fetchBlobAppend(formData, content_guid, value, filename)]
        } else if (content_guids && content_guids.length) {
          return content_guids.map(({ url, content_guid, filename }) =>
            fetchBlobAppend(formData, content_guid, url, filename)
          )
        }
      })
  )

  return formData
}

// entries with content_guid should not have values, and vice-versa
const filterFieldProps = ({
  id,
  value,
  value_id,
  displayed,
  content_guid,
  content_guids = [],
  multi_key,
}) => {
  const field = value_id
    ? {
        multi_key,
        displayed,
        value_id,
      }
    : {
        entry_id: id,
        displayed,
        multi_key,
      }

  if (content_guid) {
    return { ...field, content_guid }
  } else if (content_guids.length) {
    return {
      ...field,
      content_guids: content_guids.map(({ content_guid }) => content_guid),
    }
  }
  return { ...field, value }
}

export const annotateValidPreviewEntries = ({
  previewEntries,
  validSheetIds,
  conditions,
}) => {
  // We need to do a first pass on the previewEntries to see which ones are visible
  // or hidden and then only pass the visible ones to the final check below.
  const visibleEntries = previewEntries.filter(entry => {
    const { rowGuid, parentGuid, guid } = entry
    return conditionCheck(
      parentGuid || guid,
      previewEntries,
      conditions,
      rowGuid || false
    )
  })

  return previewEntries.map(entry => {
    const { sheet_id, rowGuid, parentGuid, guid } = entry
    const displayed =
      validSheetIds.includes(sheet_id) &&
      conditionCheck(
        parentGuid || guid,
        visibleEntries,
        conditions,
        rowGuid || false
      )
    return { ...entry, displayed }
  })
}

export const assembleSubmission = async ({
  formMetaData,
  entryData,
  savedEntryData = [],
  webclientsub = 'web-client',
  recipients = [],
}) => {
  const {
    formVersion,
    formId,
    guid,
    finalize,
    submission_name,
    lastSheetId,
    workflow,
    workflow_context,
    dispatch_id,
    department_id,
  } = formMetaData
  const formData = new FormData()
  const shouldFinalize = finalize ?? true

  // eventually edits will need to pass this in
  formData.append('guid', v4())

  formData.append('finalize', shouldFinalize)
  formData.append('webclientsub', webclientsub)

  if (workflow) {
    formData.append('workflow', JSON.stringify(workflow))
  }

  if (dispatch_id) {
    formData.append('dispatch_id', dispatch_id)
  }

  if (department_id != null) {
    formData.append('department_id', department_id)
  }

  if (!finalize) {
    formData.append('submission_name', submission_name)
    formData.append('LastSheetID', lastSheetId)
  }

  if (workflow_context) {
    formData.append('workflow_context', JSON.stringify(workflow_context))
  }

  formData.append(
    'form',
    JSON.stringify({
      id: formId,
      guid,
      version: formVersion,
      type: 'form',
      webclientsub,
    })
  )
  formData.append('recipients', JSON.stringify(recipients))

  const associateLoops = entryData => {
    return entryData.map(
      ({
        parentGuid,
        keyfieldGuid,
        rowGuid,
        nestedRowGuid,
        keyField,
        ...entry
      }) => {
        if (parentGuid && parentGuid !== keyfieldGuid) {
          const parentEntry = entryData.find(
            parentEntry =>
              (parentEntry.rowGuid == rowGuid ||
                parentEntry.nestedRowGuid == rowGuid) &&
              parentEntry.parentGuid == keyfieldGuid
          )

          return fieldTypeTransforms({
            multi_key: parentEntry.value,
            ...entry,
          })
        }

        return fieldTypeTransforms(entry)
      }
    )
  }

  const transformedData = associateLoops(entryData)
  const responses = transformedData.map(filterFieldProps)

  // Check for items that exist in saved data but not in current data and mark them for deletion
  if (savedEntryData.length) {
    const currentValueIds = new Set(responses.map(({ value_id }) => value_id))
    const deletedItems = savedEntryData.reduce((acc, { value_id }) => {
      if (!currentValueIds.has(value_id)) {
        acc.push({ value_id, delete: true })
      }
      return acc
    }, [])

    responses.push(...deletedItems)
  }

  formData.append('responses', JSON.stringify(responses))

  return await extractFiles(formData, transformedData)
}

export const createAPISubmission = async ({
  formId,
  formVersion,
  finalize = true,
  submissionId,
  submission_name = '',
  lastSheetId,
  savedPreviewEntries,
  previewEntries,
  recipients = [],
  webclientsub = 'web-client',
  workflow = null,
  workflow_context = null,
  dispatch_id = null,
  department_id = null,
}) => {
  const submissionEndpoint = submissionId
    ? `/api/v3/submissions/${submissionId}/web_update`
    : '/api/v3/submissions'

  const submissionMethod = submissionId ? patch : post

  const formMetaData = {
    formId,
    formVersion,
    finalize,
    submission_name,
    lastSheetId,
    workflow,
    workflow_context,
    dispatch_id,
    department_id,
  }

  const submissionData = await assembleSubmission({
    formMetaData,
    entryData: previewEntries,
    savedEntryData: savedPreviewEntries,
    webclientsub,
    recipients,
  })

  return submissionMethod(
    submissionEndpoint,
    submissionData,
    buildHeaders(true)
  )
}

export const findChildSheetIds = (sheets, multiSectionId) => {
  const childSheets = []
  const children = sheets.reduce((ids, sheet) => {
    if (sheet.section_id === multiSectionId) {
      ids.push(sheet.id)
      childSheets.push(sheet)
    }
    return ids
  }, [])
  const grandChildren = childSheets.reduce((acc, { multi_section }) => {
    if (multi_section) {
      acc.push(...findChildSheetIds(sheets, multi_section))
    }
    return acc
  }, [])

  return [...children, ...grandChildren]
}

const processEntryValue = (entry, response) => {
  const isMediaType = [
    'Photo',
    'Signature',
    'Drawing',
    'Attachment',
    'Video',
  ].includes(entry.type)

  const value = isMediaType ? response.value : getValue(entry.type, response.value)

  return {
    ...entry,
    ...response,
    type: entry.type,
    value,
    displayed: true,
    touched: value ? true : false,
  }
}

const processLoopScreen = (
  sheet,
  submissionResponses,
  sheets,
  entries,
  parentKeyFieldValue = null,
  parentRowGuid = null
) => {
  const loopEntries = []
  const sheetEntries = entries.filter(e => e.sheet_id === sheet.id)
  const keyFieldEntry = sheetEntries[0]
  // If parentKeyFieldValue exists, we are processing a nested loop
  const keyFieldResponses = parentKeyFieldValue
    ? submissionResponses.filter(
        r =>
          r.entry_id === keyFieldEntry.id && r.multi_key === parentKeyFieldValue
      )
    : submissionResponses.filter(r => r.entry_id === keyFieldEntry.id)

  const childSheet = sheets.find(s => s.section_id === sheet.multi_section)
  const childSheetEntries = isList(sheet)
    ? [] // Lists do not have a child sheet
    : entries.filter(e => e.sheet_id === childSheet.id)

  keyFieldResponses.forEach(response => {
    const rowGuid = parentRowGuid || guid()
    const nestedRowGuid = parentRowGuid ? guid() : null

    loopEntries.push({
      ...processEntryValue(keyFieldEntry, response),
      guid: guid(),
      rowGuid,
      nestedRowGuid,
      parentGuid: keyFieldEntry.guid,
      keyfieldGuid: keyFieldEntry.guid,
      keyField: true,
    })

    if (!isList(sheet)) {
      childSheetEntries.forEach(entry => {
        const childResponse =
          submissionResponses.find(
            r => r.entry_id === entry.id && r.multi_key === response.value
          ) || {}

        loopEntries.push({
          ...processEntryValue(entry, childResponse),
          guid: guid(),
          rowGuid: nestedRowGuid || rowGuid,
          parentGuid: entry.guid,
          keyfieldGuid: keyFieldEntry.guid,
        })
      })
    }
  })

  return loopEntries
}

const processNormalScreen = (
  entries,
  submissionResponses,
  sheet,
  parentKeyFieldValue = null,
  parentKeyFieldGuid = null,
  parentRowGuid = null
) => {
  const normalEntries = []
  const sheetEntries = entries.filter(e => e.sheet_id === sheet.id)

  const getLoopAttributes = entry => {
    if (!parentKeyFieldValue) return {}

    return {
      guid: guid(),
      rowGuid: parentRowGuid,
      parentGuid: entry.guid,
      keyfieldGuid: parentKeyFieldGuid,
    }
  }

  sheetEntries.forEach(entry => {
    const response = parentKeyFieldValue
      ? submissionResponses.find(
          r => r.entry_id === entry.id && r.multi_key === parentKeyFieldValue
        )
      : submissionResponses.find(r => r.entry_id === entry.id)
    const loopAttributes = getLoopAttributes(entry)
    const baseEntry = response ? processEntryValue(entry, response) : entry

    normalEntries.push({
      ...baseEntry,
      ...loopAttributes,
    })
  })

  return normalEntries
}

// Recursive function to process advanced loops (can be nested)
const processAdvancedLoopScreen = (
  sheets,
  entries,
  submissionResponses,
  sheet,
  advancedLoopEntries,
  parentKeyfieldValue,
  parentRowGuid
) => {
  const sheetEntries = entries.filter(e => e.sheet_id === sheet.id)
  const keyFieldEntry = sheetEntries[0]

  // Get key field responses for this loop level
  // If this is a nested loop, filter by multi_key matching parent keyfield value
  const keyFieldResponses = parentKeyfieldValue
    ? submissionResponses.filter(
        r =>
          r.entry_id === keyFieldEntry.id && r.multi_key === parentKeyfieldValue
      )
    : submissionResponses.filter(r => r.entry_id === keyFieldEntry.id)

  // Find child sheet(s) for this loop
  const childSheets = sheets.filter(s => s.section_id === sheet.multi_section)

  keyFieldResponses.forEach(response => {
    const rowGuid = guid()
    const keyfieldValue = response.value

    // Add the keyfield entry for this row
    advancedLoopEntries.push({
      ...processEntryValue(keyFieldEntry, response),
      guid: guid(),
      rowGuid: parentRowGuid || rowGuid,
      nestedRowGuid: parentRowGuid ? rowGuid : null,
      parentGuid: keyFieldEntry.guid,
      keyfieldGuid: keyFieldEntry.guid,
      keyField: true,
    })

    childSheets.forEach(childSheet => {
      // Process child sheet - could be another advanced loop, simple loop, or regular sheet
      if (isAdvancedLoop(childSheet)) {
        // Recursively process nested advanced loop
        // Pass the current keyfield value and rowGuid so nested entries can link back
        processAdvancedLoopScreen(
          sheets,
          entries,
          submissionResponses,
          childSheet,
          advancedLoopEntries,
          keyfieldValue,
          rowGuid
        )
      } else if (isLoop(childSheet)) {
        // Process simple loop within advanced loop
        const loopEntries = processLoopScreen(
          childSheet,
          submissionResponses,
          sheets,
          entries,
          keyfieldValue,
          rowGuid
        )
        advancedLoopEntries.push(...loopEntries)
      } else {
        const normalEntries = processNormalScreen(
          entries,
          submissionResponses,
          childSheet,
          keyfieldValue,
          keyFieldEntry.guid,
          rowGuid
        )
        advancedLoopEntries.push(...normalEntries)
      }
    })
  })
  return advancedLoopEntries
}

export const populateEntriesWithSavedValues = (
  sheets,
  sections,
  entries,
  submissionResponses
) => {
  const orderedTopLevelSheets = getTopLevelSheets(sheets, sections)
  const clientEntries = []

  orderedTopLevelSheets.forEach(sheet => {
    if (isAdvancedLoop(sheet)) {
      const advancedLoopEntries = []
      processAdvancedLoopScreen(
        sheets,
        entries,
        submissionResponses,
        sheet,
        advancedLoopEntries,
        null,
        null
      )
      clientEntries.push(...advancedLoopEntries)
    } else if (isLoop(sheet)) {
      const loopEntries = processLoopScreen(
        sheet,
        submissionResponses,
        sheets,
        entries
      )
      clientEntries.push(...loopEntries)
    } else {
      const normalEntries = processNormalScreen(
        entries,
        submissionResponses,
        sheet
      )
      clientEntries.push(...normalEntries)
    }
  })

  return clientEntries
}

// filterClientEntries :: (Array, Array) -> Array
// Filters out entries that are part of loop or table sheets; those should not be initialized
export const filterClientEntries = (sheets, entries) => {
  const loopSheetIds = sheets.reduce((acc, sheet) => {
    const { sheet_type_id } = sheet
    if (LOOP_SHEET_TYPE_IDS.includes(sheet_type_id)) {
      const childSheetIds = findChildSheetIds(sheets, sheet.multi_section)
      acc.push(sheet.id, ...childSheetIds)
    }
    return acc
  }, [])

  return entries.filter(({ sheet_id }) => !loopSheetIds.includes(sheet_id))
}

const getSectionInformation = (sections, { section_id, ...sheet }) => {
  const { position } = sections.find(({ id }) => id === section_id) || {}
  const updateSheet = {
    ...sheet,
    section_id,
    position,
  }
  return updateSheet
}

const getTopLevelSheets = (sheets, sections) => {
  const multiSectionIds = sheets.reduce((acc, { multi_section }) => {
    if (multi_section) acc.push(multi_section)
    return acc
  }, [])

  return sheets
    .reduce((acc, sheet) => {
      if (!multiSectionIds.includes(sheet.section_id)) {
        acc.push(getSectionInformation(sections, sheet))
      }
      return acc
    }, [])
    .sort(orderByPosition)
}
